export type RgbColor = { r: number; g: number; b: number };

export const CSS_NAMED_COLOR_HEX_VALUES = {
  aliceblue: "#f0f8ff",
  antiquewhite: "#faebd7",
  aqua: "#00ffff",
  aquamarine: "#7fffd4",
  azure: "#f0ffff",
  beige: "#f5f5dc",
  bisque: "#ffe4c4",
  black: "#000000",
  blanchedalmond: "#ffebcd",
  blue: "#0000ff",
  blueviolet: "#8a2be2",
  brown: "#a52a2a",
  burlywood: "#deb887",
  cadetblue: "#5f9ea0",
  chartreuse: "#7fff00",
  chocolate: "#d2691e",
  coral: "#ff7f50",
  cornflowerblue: "#6495ed",
  cornsilk: "#fff8dc",
  crimson: "#dc143c",
  cyan: "#00ffff",
  darkblue: "#00008b",
  darkcyan: "#008b8b",
  darkgoldenrod: "#b8860b",
  darkgray: "#a9a9a9",
  darkgreen: "#006400",
  darkgrey: "#a9a9a9",
  darkkhaki: "#bdb76b",
  darkmagenta: "#8b008b",
  darkolivegreen: "#556b2f",
  darkorange: "#ff8c00",
  darkorchid: "#9932cc",
  darkred: "#8b0000",
  darksalmon: "#e9967a",
  darkseagreen: "#8fbc8f",
  darkslateblue: "#483d8b",
  darkslategray: "#2f4f4f",
  darkslategrey: "#2f4f4f",
  darkturquoise: "#00ced1",
  darkviolet: "#9400d3",
  deeppink: "#ff1493",
  deepskyblue: "#00bfff",
  dimgray: "#696969",
  dimgrey: "#696969",
  dodgerblue: "#1e90ff",
  firebrick: "#b22222",
  floralwhite: "#fffaf0",
  forestgreen: "#228b22",
  fuchsia: "#ff00ff",
  gainsboro: "#dcdcdc",
  ghostwhite: "#f8f8ff",
  gold: "#ffd700",
  goldenrod: "#daa520",
  gray: "#808080",
  green: "#008000",
  greenyellow: "#adff2f",
  grey: "#808080",
  honeydew: "#f0fff0",
  hotpink: "#ff69b4",
  indianred: "#cd5c5c",
  indigo: "#4b0082",
  ivory: "#fffff0",
  khaki: "#f0e68c",
  lavender: "#e6e6fa",
  lavenderblush: "#fff0f5",
  lawngreen: "#7cfc00",
  lemonchiffon: "#fffacd",
  lightblue: "#add8e6",
  lightcoral: "#f08080",
  lightcyan: "#e0ffff",
  lightgoldenrodyellow: "#fafad2",
  lightgray: "#d3d3d3",
  lightgreen: "#90ee90",
  lightgrey: "#d3d3d3",
  lightpink: "#ffb6c1",
  lightsalmon: "#ffa07a",
  lightseagreen: "#20b2aa",
  lightskyblue: "#87cefa",
  lightslategray: "#778899",
  lightslategrey: "#778899",
  lightsteelblue: "#b0c4de",
  lightyellow: "#ffffe0",
  lime: "#00ff00",
  limegreen: "#32cd32",
  linen: "#faf0e6",
  magenta: "#ff00ff",
  maroon: "#800000",
  mediumaquamarine: "#66cdaa",
  mediumblue: "#0000cd",
  mediumorchid: "#ba55d3",
  mediumpurple: "#9370db",
  mediumseagreen: "#3cb371",
  mediumslateblue: "#7b68ee",
  mediumspringgreen: "#00fa9a",
  mediumturquoise: "#48d1cc",
  mediumvioletred: "#c71585",
  midnightblue: "#191970",
  mintcream: "#f5fffa",
  mistyrose: "#ffe4e1",
  moccasin: "#ffe4b5",
  navajowhite: "#ffdead",
  navy: "#000080",
  oldlace: "#fdf5e6",
  olive: "#808000",
  olivedrab: "#6b8e23",
  orange: "#ffa500",
  orangered: "#ff4500",
  orchid: "#da70d6",
  palegoldenrod: "#eee8aa",
  palegreen: "#98fb98",
  paleturquoise: "#afeeee",
  palevioletred: "#db7093",
  papayawhip: "#ffefd5",
  peachpuff: "#ffdab9",
  peru: "#cd853f",
  pink: "#ffc0cb",
  plum: "#dda0dd",
  powderblue: "#b0e0e6",
  purple: "#800080",
  rebeccapurple: "#663399",
  red: "#ff0000",
  rosybrown: "#bc8f8f",
  royalblue: "#4169e1",
  saddlebrown: "#8b4513",
  salmon: "#fa8072",
  sandybrown: "#f4a460",
  seagreen: "#2e8b57",
  seashell: "#fff5ee",
  sienna: "#a0522d",
  silver: "#c0c0c0",
  skyblue: "#87ceeb",
  slateblue: "#6a5acd",
  slategray: "#708090",
  slategrey: "#708090",
  snow: "#fffafa",
  springgreen: "#00ff7f",
  steelblue: "#4682b4",
  tan: "#d2b48c",
  teal: "#008080",
  thistle: "#d8bfd8",
  tomato: "#ff6347",
  turquoise: "#40e0d0",
  violet: "#ee82ee",
  wheat: "#f5deb3",
  white: "#ffffff",
  whitesmoke: "#f5f5f5",
  yellow: "#ffff00",
  yellowgreen: "#9acd32",
} as const satisfies Record<string, string>;

export const normalizeCssNamedColor = (value: string): string | null => {
  const key = value.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(CSS_NAMED_COLOR_HEX_VALUES, key)
    ? CSS_NAMED_COLOR_HEX_VALUES[key as keyof typeof CSS_NAMED_COLOR_HEX_VALUES]
    : null;
};

export const normalizeHexColor = (value: string): string | null => {
  const trimmed = value.trim();
  const shortMatch = trimmed.match(/^#?([0-9a-f]{3})$/i);
  if (shortMatch) {
    const hex = shortMatch[1] ?? "";
    return `#${hex
      .split("")
      .map((char) => `${char}${char}`)
      .join("")}`.toLowerCase();
  }

  const longMatch = trimmed.match(/^#?([0-9a-f]{6})$/i);
  return longMatch ? `#${(longMatch[1] ?? "").toLowerCase()}` : null;
};

export const resolveCssColorHex = (value: string): string | null =>
  normalizeHexColor(value) ?? normalizeCssNamedColor(value);

export const hexToRgb = (value: string): RgbColor | null => {
  const normalized = normalizeHexColor(value);
  if (!normalized) {
    return null;
  }

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
};

const BRAND_LOGO_LIGHT_CARRIER_FILL = "#ffffff";
const BRAND_LOGO_DARK_CARRIER_FILL = "#111827";
const MIN_CONTRAST_ON_LIGHT_LOGO_CARRIER = 1.35;

const toLinearRgbChannel = (channel: number): number => {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
};

const getRelativeLuminance = (color: string): number | null => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return null;
  }

  return (
    0.2126 * toLinearRgbChannel(rgb.r) +
    0.7152 * toLinearRgbChannel(rgb.g) +
    0.0722 * toLinearRgbChannel(rgb.b)
  );
};

const getContrastRatio = (foreground: string, background: string): number | null => {
  const left = getRelativeLuminance(foreground);
  const right = getRelativeLuminance(background);
  if (left === null || right === null) {
    return null;
  }

  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
};

const hasLowContrastOnLightLogoCarrier = (color: string): boolean => {
  const contrast = getContrastRatio(color, BRAND_LOGO_LIGHT_CARRIER_FILL);
  return contrast !== null && contrast < MIN_CONTRAST_ON_LIGHT_LOGO_CARRIER;
};

export const resolveBrandLogoCarrierFill = (
  colorPalette: readonly (string | null | undefined)[] | null | undefined,
): string => {
  const colors = (colorPalette ?? [])
    .map((color) => (typeof color === "string" ? normalizeHexColor(color) : null))
    .filter((color): color is string => Boolean(color));

  if (colors.length === 0) {
    return BRAND_LOGO_LIGHT_CARRIER_FILL;
  }

  return colors.every(hasLowContrastOnLightLogoCarrier)
    ? BRAND_LOGO_DARK_CARRIER_FILL
    : BRAND_LOGO_LIGHT_CARRIER_FILL;
};

export const rgbColorDistance = (left: RgbColor, right: RgbColor): number =>
  Math.sqrt(
    Math.pow(left.r - right.r, 2) + Math.pow(left.g - right.g, 2) + Math.pow(left.b - right.b, 2),
  );

export const hexColorDistance = (left: string, right: string): number => {
  const leftRgb = hexToRgb(left);
  const rightRgb = hexToRgb(right);
  if (!leftRgb || !rightRgb) {
    return Number.POSITIVE_INFINITY;
  }

  return rgbColorDistance(leftRgb, rightRgb);
};
